import {ChangeEvent, FormEvent, useRef, useState} from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import axios from "axios";
import { track } from "./analytics/track";
import { consumeArticleReferrer } from "./analytics/attribution";
interface FormData {
  /** The text to display inside the button */
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  enquiry: string;
  token: string;
}

interface Errors {
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
  enquiry?: string;
  token?: string;
}

const Contact = () => {

  const turnstileRef = useRef<any>(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const sender = process.env.REACT_APP_SENDER;
  const recipient = process.env.REACT_APP_RECIPIENT;
  const [statusMessage, setStatusMessage] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    enquiry: "",
    token: "",
  });

  const [errors, setErrors] = useState<Errors>({});


  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value, // Dynamically update the field based on name attribute
    }));
  };

  const handleTokenSuccess = (receivedToken: string) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      token: receivedToken, // Update the token in the form data
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};
    let isValid = true;

    if (!formData.token) {
      newErrors.token = "Verification is required";
      isValid = false;
    }
    if (!formData.firstname) {
      newErrors.firstname = "First Name is required";
      isValid = false;
    }
    if (!formData.lastname) {
      newErrors.lastname = "Last Name is required";
      isValid = false;
    }
    if (!formData.enquiry) {
      newErrors.enquiry = "Enquiry is required";
      isValid = false;
    } else if (formData.enquiry.length < 10) {
      newErrors.enquiry = "The enquiry must be at least 10 characters long.";
      isValid = false;
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailPattern.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);

      console.log("Form submitted successfully:", formData);
      try {
        axios.post(
          `${apiUrl}`, {
            origin: "newdawncounselling.uk",
            sender: sender,
            recipient: recipient,
            "cf-turnstile-response": formData.token,
            name: `${formData.firstname} ${formData.lastname}`,
            email: formData.email,
            mobile: formData.phone,
            message: formData.enquiry,
          }
        ).then(async response => {
          if (response.status === 200) {
            setIsSuccess(true);
            track("contact-submit", { source: consumeArticleReferrer() });
            setStatusMessage("Email sent successfully!");
            setFormData({
              firstname: "",
              lastname: "",
              email: "",
              phone: "",
              enquiry: "",
              token: "",
            });
              turnstileRef.current?.reset()
            setTimeout(() => {
              setStatusMessage("");
              setIsSuccess(false);
            }, 5000);
          }
        })
          .catch(error => {
            console.error('Error:', error);
          })
        .finally(() => {
          setLoading(false);
        })

      }
      finally {
      }
    }
  };

  return (
    <section id="contact" className="bg-white px-6 py-16 md:px-10 md:py-24">
      <div className='mx-auto w-full max-w-2xl space-y-6'>
        <div className="space-y-4 text-center">
          <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
            Contact
          </p>
          <h2 className='text-4xl md:text-5xl font-trirong_light text-ink'>Please do get in touch…</h2>
        </div>
        <p className='text-base leading-relaxed font-trirong_light text-ink-soft'>I will get back to you as soon as possible.</p>
        <p className='text-base leading-relaxed font-trirong_light text-ink-soft'>Please give me your telephone number or be sure to check your spam for my reply.</p>
        <form onSubmit={handleSubmit} className='text-left'>
          <div className="mb-5 space-y-4">
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="flex-1">
                <label htmlFor="first-name" className="block mb-2 font-nunito font-semibold text-sm text-ink-soft">First Name</label>
                <input type="text" id="first-name" value={formData.firstname} name="firstname"
                  onChange={handleChange} className="w-full rounded-xl border border-hairline bg-white px-4 py-3 font-nunito text-ink outline-none transition-colors focus:border-sage focus:ring-2 focus:ring-sage/30" />
                {errors.firstname && <span className="mt-1 inline-block font-nunito text-sm text-danger">{errors.firstname}</span>}

              </div>
              <div className="flex-1">
                <label htmlFor="last-name" className="block mb-2 font-nunito font-semibold text-sm text-ink-soft">Last Name</label>
                <input type="text" id="last-name" value={formData.lastname} name="lastname"
                  onChange={handleChange} className="w-full rounded-xl border border-hairline bg-white px-4 py-3 font-nunito text-ink outline-none transition-colors focus:border-sage focus:ring-2 focus:ring-sage/30" />
                {errors.lastname && <span className="mt-1 inline-block font-nunito text-sm text-danger">{errors.lastname}</span>}

              </div>
            </div>
            <div>
              <label htmlFor="email" className="block mb-2 font-nunito font-semibold text-sm text-ink-soft">Email</label>
              <input type="email" id="email" value={formData.email} pattern=".*" name="email"
                onChange={handleChange} style={{ textTransform: 'none' }}
                className="w-full rounded-xl border border-hairline bg-white px-4 py-3 font-nunito text-ink outline-none transition-colors focus:border-sage focus:ring-2 focus:ring-sage/30" />
              {errors.email && <span className="mt-1 inline-block font-nunito text-sm text-danger">{errors.email}</span>}
            </div>
            <div>
              <label htmlFor="phone" className="block mb-2 font-nunito font-semibold text-sm text-ink-soft">Telephone Number (optional)</label>
              <input type="tel" id="phone" value={formData.phone} name="phone"
                onChange={handleChange}
                className="w-full rounded-xl border border-hairline bg-white px-4 py-3 font-nunito text-ink outline-none transition-colors focus:border-sage focus:ring-2 focus:ring-sage/30" />
            </div>
            <div>
              <label htmlFor="enquiry" className="block mb-2 font-nunito font-semibold text-sm text-ink-soft">Please tell me a little bit about your enquiry</label>
              <textarea  value={formData.enquiry} name="enquiry"
                onChange={handleChange} rows={4}
                className="w-full rounded-xl border border-hairline bg-white px-4 py-3 font-nunito text-ink outline-none transition-colors focus:border-sage focus:ring-2 focus:ring-sage/30 resize-none" />
              {errors.enquiry && <span className="mt-1 inline-block font-nunito text-sm text-danger">{errors.enquiry}</span>}
            </div>
            <div>
              <Turnstile ref={turnstileRef} siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || ""}
                 onSuccess={handleTokenSuccess}
                 options={{
                  theme: 'light',
                }}
              />{" "}
              {errors.token && <span className="mt-1 inline-block font-nunito text-sm text-danger">{errors.token}</span>}
            </div>
            <div className="flex justify-center items-center mt-4">
              <button className='rounded-full bg-sage px-9 py-3.5 font-nunito font-bold text-sm text-white transition-colors hover:bg-sage-deep disabled:opacity-70'>
                {isLoading ? (
                    <div className='flex items-center justify-center opacity-70'>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="currentColor"
                              d="M12 2A10 10 0 1 0 22 12A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z"
                              opacity="0.5"/>
                        <path fill="currentColor" d="M20 12h2A10 10 0 0 0 12 2V4A8 8 0 0 1 20 12Z">
                          <animateTransform attributeName="transform" dur="1s" from="0 12 12" repeatCount="indefinite"
                                            to="360 12 12" type="rotate"/>
                        </path>
                      </svg>
                    </div>
                ) : (
                    <p>Submit</p>
                )
                }
              </button>
            </div>
            {statusMessage && (
                <p className="font-trirong_light" style={{color: isSuccess ? "green" : "red"}}>{statusMessage}</p>
            )}
          </div>

        </form>

      </div>

    </section>
  );
}

export default Contact;